import { getPostedThisMonth } from '@/features/dashboard/dashboardData';
import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FilterBar,
  NumberedPager,
  ListEmpty,
  DEFAULT_HISTORY_PRESET,
  type HistoryDatePreset,
  useListUrlState,
  windowLabel,
  applySearch,
  sortBy,
  paginate,
} from '@/components/lists';
import { money, signedMoney, amountAria, wholeMoney, cardChargedLine } from '../utils';
import {
  buildPostedHistory,
  historyPayers,
  POSTED_KINDS,
  CARD_FEE_LABEL,
  type PostedHistoryRow,
} from '../historyMock';
import { inRange, presetRange, shortDate } from '../dates';
import { CardSwitchCards } from './CardSwitch';
import { creditAsOfIso, creditDates, daysBetween, isCaliforniaScenario, objectPronoun } from '../creditMock';
import { getPaidToPatientRowsSync, getToPostSync } from '../store';
import { PMS } from '@/features/shared/pms';

type Show = '' | 'credit' | 'paid_to_patient';
const SHOW_OPTIONS: { value: Show; label: string }[] = [
  { value: '', label: 'All postings' },
  { value: 'credit', label: 'Left a patient credit' },
  { value: 'paid_to_patient', label: 'Paid to the patient' },
];

/** Where a credit stands now, read from the PMS (H1 §5). */
function CreditNow({ r }: { r: PostedHistoryRow }) {
  const c = r.credit;
  if (!c) return null;
  const first = r.patient.split(' ')[0] ?? r.patient;
  if (c.state === 'refunded') return <span className="po-badge b-mute">Refunded {c.stateIso ? shortDate(c.stateIso) : ''} in {PMS}</span>;
  if (c.state === 'used') return <span className="po-badge b-mute">Used on the visit of {c.stateIso ? shortDate(c.stateIso) : ''}</span>;
  const ca = isCaliforniaScenario();
  const d = creditDates(c.sinceIso, ca);
  const days = daysBetween(c.sinceIso, creditAsOfIso());
  return (
    <span className="po-credit-now">
      <span className="po-badge b-info">Still on {first}’s account · {days} {days === 1 ? 'day' : 'days'}</span>
      <span className="po-meta">
        {ca && d.tellByIso
          ? `tell ${objectPronoun(c.pronoun)} by ${shortDate(d.tellByIso)} · in Human in the loop`
          : `reminder on ${shortDate(d.remindIso)}`}
      </span>
    </span>
  );
}

const PAGE_SIZE = 50;

/**
 * Posted — the history view (00-ds-09 §9, D148). Entirely client-side over the
 * seeded mock: a FilterBar (search · date range back to go-live · payer · kind
 * of payment) + a table + a numbered pager, 50 a page. Default window is this
 * month; state lives in the URL so links and Back work.
 */
export function PostedHistory() {
  const navigate = useNavigate();
  const [s, patch] = useListUrlState({
    q: '',
    range: DEFAULT_HISTORY_PRESET,
    payer: '',
    kind: '',
    show: '',
    page: '1',
  });
  const show: Show = s.show === 'credit' || s.show === 'paid_to_patient' ? s.show : '';
  const all = useMemo(() => buildPostedHistory(), []);
  const paidToPatientNow = getPaidToPatientRowsSync();
  const waitingPaidToPatient = getToPostSync().filter((t) => t.kind === 'paid_to_patient').length;
  const payers = useMemo(() => historyPayers(all), [all]);

  const preset = s.range as HistoryDatePreset;
  const range = presetRange(preset);
  const page = Math.max(1, Number(s.page) || 1);

  const filtered = useMemo(() => {
    const source: PostedHistoryRow[] =
      show === 'credit' ? all.filter((r) => r.credit) : show === 'paid_to_patient' ? [...all.filter((r) => r.paidToPatient), ...paidToPatientNow] : all;
    let rows: PostedHistoryRow[] = source.filter((r) => inRange(r.dateIso, range));
    if (s.payer) rows = rows.filter((r) => r.payer === s.payer);
    if (s.kind) rows = rows.filter((r) => r.kind === s.kind);
    rows = applySearch(
      rows,
      s.q,
      (r) => `${r.patient} ${r.payer} ${money(r.amount)} ${String(r.amount)} ${r.trace} ${r.claimNumber} ${r.checkNo ?? ''}`,
    );
    return sortBy(rows, (r) => r.dateIso, -1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, show, paidToPatientNow, range.from, range.to, s.payer, s.kind, s.q]);

  const total = filtered.length;
  // "3 left a patient credit this month": counted from the rows, never typed.
  const creditMonth = useMemo(() => {
    const r = presetRange('this-month');
    const rows = all.filter((x) => x.credit && inRange(x.dateIso, r));
    return { count: rows.length, total: Math.round(rows.reduce((t, x) => t + x.amount, 0) * 100) / 100 };
  }, [all]);
  const rows = paginate(filtered, page, PAGE_SIZE);
  const monthTotal = filtered.reduce((sum, r) => sum + r.amount, 0);
  // H11: the unfiltered month view shows the same figure as the Dashboard's "Insurance payments posted".
  const monthSummary = preset === 'this-month' && !s.q && !s.payer && !s.kind && !show ? getPostedThisMonth() : null;

  const kindSelect = (
    <select
      className="lx-select"
      aria-label="Kind of payment"
      value={s.kind}
      onChange={(e) => { patch({ kind: e.target.value, page: '1' }); }}
    >
      <option value="">All kinds</option>
      {POSTED_KINDS.map((k) => (
        <option key={k} value={k}>{k}</option>
      ))}
    </select>
  );
  const showSelect = (
    <select
      className="lx-select"
      aria-label="Show"
      value={show}
      onChange={(e) => { patch({ show: e.target.value, page: '1' }); }}
    >
      {SHOW_OPTIONS.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
  const payerSelect = (
    <select
      className="lx-select"
      aria-label="Payer"
      value={s.payer}
      onChange={(e) => { patch({ payer: e.target.value, page: '1' }); }}
    >
      <option value="">All payers</option>
      {payers.map((p) => (
        <option key={p} value={p}>{p}</option>
      ))}
    </select>
  );

  return (
    <div className="po-wide">
      <CardSwitchCards />
      <FilterBar
        search={s.q}
        onSearch={(v) => { patch({ q: v, page: '1' }); }}
        searchPlaceholder="Search patient, payer, amount, trace or claim"
        dateRange={preset}
        onDateRange={(v) => { patch({ range: v, page: '1' }); }}
      >
        {showSelect}
        {payerSelect}
        {kindSelect}
      </FilterBar>

      <p className="po-meta" style={{ margin: 0 }}>
        {total === 0
          ? `No payments in ${windowLabel(preset)}.`
          : monthSummary
            ? <><strong>{wholeMoney(monthSummary.total)}</strong> in insurance payments posted this month · of which ELVA posted {wholeMoney(monthSummary.elvaPosted)}.</>
            : <><strong>{total.toLocaleString()}</strong> payments posted in {windowLabel(preset)}, {wholeMoney(monthTotal)}.</>}
      </p>
      {creditMonth.count > 0 ? (
        <p className="po-meta" style={{ margin: 0 }}>
          {creditMonth.count} left a patient credit this month · {money(creditMonth.total)} ·{' '}
          <button
            type="button"
            className="po-src"
            style={{ textDecoration: 'underline' }}
            onClick={() => { patch({ show: 'credit', range: 'this-month', payer: '', kind: '', q: '', page: '1' }); }}
          >
            Show them
          </button>
        </p>
      ) : null}

      {total === 0 && show === 'paid_to_patient' ? (
        <section className="po-card">
          <p style={{ margin: 0 }}>No postings where the payer paid the patient in {windowLabel(preset)}.</p>
          {waitingPaidToPatient > 0 ? <p className="po-meta" style={{ margin: '6px 0 0' }}>{waitingPaidToPatient} waiting in To post.</p> : null}
        </section>
      ) : total === 0 ? (
        <section className="po-card">
          <ListEmpty
            query={s.q || undefined}
            windowLabel={windowLabel(preset)}
            {...(preset !== 'all-time'
              ? { onSearchAllTime: () => { patch({ range: 'all-time', page: '1' }); } }
              : {})}
          />
        </section>
      ) : (
        <>
          {show === 'credit' ? (
            <section className="po-card" style={{ padding: 0, overflow: 'hidden' }}>
              <div className="po-tbl-wrap">
                <table className="po-tbl po-history-tbl">
                  <caption className="po-vh">Postings that left a patient credit</caption>
                  <thead>
                    <tr><th>Patient</th><th>Payer</th><th className="ta-right">Credit</th><th>Now</th></tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="po-rowlink" onClick={() => { void navigate(`/posting/posted/${r.id}`); }}>
                        <td>
                          <Link to={`/posting/posted/${r.id}`} className="po-namelink" onClick={(e) => { e.stopPropagation(); }}><strong>{r.patient}</strong></Link>
                          <span className="po-cardlabel">posted {shortDate(r.dateIso)} · {r.reference ?? ''}</span>
                        </td>
                        <td>{r.payer}</td>
                        <td className="ta-right num" aria-label={amountAria(r.amount)}>{money(r.amount)}</td>
                        <td><CreditNow r={r} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="po-meta" style={{ padding: '10px 16px', margin: 0, borderTop: '1px solid var(--border)' }}>
                Total {money(filtered.reduce((t, r) => t + r.amount, 0))} · read from {PMS} each sync. ELVA reports credits; it never refunds or moves them.
              </p>
            </section>
          ) : (
          <section className="po-card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="po-tbl-wrap">
              <table className="po-tbl po-history-tbl">
                <thead>
                  <tr>
                    <th>Patient</th>
                    <th>Payer</th>
                    <th className="ta-right">Amount</th>
                    <th>Kind of payment</th>
                    <th>Posted date</th>
                    <th>Confirmed by</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="po-rowlink" onClick={() => { void navigate(`/posting/posted/${r.id}`); }}>
                      <td><Link to={`/posting/posted/${r.id}`} className="po-namelink" onClick={(e) => { e.stopPropagation(); }}><strong>{r.patient}</strong></Link></td>
                      <td>
                        {r.payer}
                        {r.method === 'card' ? <span className="po-cardlabel">{cardChargedLine(r.approval ?? '')}</span> : null}
                        {r.method === 'check' ? <span className="po-cardlabel">Paper check {r.checkNo}</span> : null}
                      </td>
                      <td className="ta-right num" aria-label={amountAria(r.amount)}>
                        {signedMoney(r.amount)}
                        {r.method === 'card' && r.fee != null ? <span className="po-cardlabel">{CARD_FEE_LABEL(r.fee)}</span> : null}
                        {r.note ? <span className="po-cardlabel">{r.note}</span> : null}
                      </td>
                      <td>
                        {r.state === 'already_posted' ? <span className="po-badge b-mute">Already posted in {PMS}</span> : r.kind}
                        {r.state === 'already_posted' ? <span className="po-cardlabel">ELVA linked it and didn’t post again</span> : null}
                        {r.reference ? <span className="po-cardlabel">{r.reference}</span> : null}
                      </td>
                      <td>{r.dateLabel}</td>
                      <td>{r.confirmedBy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          )}
          <NumberedPager
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            onPage={(n) => { patch({ page: String(n) }); }}
          />
        </>
      )}
    </div>
  );
}
